// «Точность 24h» tab — the original daily-analytics view, restored.
//
// Rollback (per operator feedback): the Phase-2 prototype `AccuracyTab`
// redesigned this tab, but it should not have changed. We revert ONLY the
// «точность» tab to its pre-2fc20a6 layout — SummaryCards, the failure
// breakdown strip, ActivityHeatmap, HeatmapStationConfig, the latitude×|dR|
// scatter, the self-assessment block and the processing-time table. The
// «сходимость» tab keeps its separate redesign, and the screen keeps the
// single `.at-tabs` bar.
//
// The screen shell owns the filter set + persistence, the 30-s auto-refresh
// (it blanket-invalidates the ['analytics'] query namespace) and the live
// indicator. This tab just renders the sections over the existing hooks
// under that namespace, so the shell's refresh tick reaches them.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import type { AnalyticsFilters as Filters } from '../../api/types';
import {
  useAnalyticsActivity,
  useAnalyticsHeatmap,
  useAnalyticsProcessingTime,
  useAnalyticsScatter,
  useAnalyticsSelfAssessment,
  useAnalyticsSummary,
} from '../../hooks/useAnalytics';
import { ANALYTICS_FAILURE_DRILL_STATE } from '../../lib/analyticsDrillState';
import { FailureBreakdownStrip } from '../FailureBreakdownStrip';
import { ActivityHeatmap } from './ActivityHeatmap';
import { AnalyticsFilters } from './AnalyticsFilters';
import { failureCategoryFeedUrl } from './failureCategoryFeedUrl';
import { HeatmapStationConfig } from './HeatmapStationConfig';
import { ProcessingTimeTable } from './ProcessingTimeTable';
import { ScatterLatitudeResidual } from './ScatterLatitudeResidual';
import { SelfAssessmentBlock } from './SelfAssessmentBlock';
import { SummaryCards } from './SummaryCards';

import './AnalyticsScreen.css';

interface Props {
  filters: Filters;
  onFilterChange: (next: Filters) => void;
}

export function Daily24hTab({ filters, onFilterChange }: Props) {
  const navigate = useNavigate();
  // Phase R — failure-category breakdown across the whole DB. Read-only,
  // no filter coupling (the daily filters are dataset-specific to the
  // aggregates; the breakdown is a global counter). Kept under the
  // ['analytics'] namespace so the shell's auto-refresh invalidates it too.
  const failureBreakdown = useQuery({
    queryKey: ['analytics', 'failure-breakdown'],
    queryFn: () => api.getFailureBreakdown(),
    staleTime: 30_000,
  });

  const summary = useAnalyticsSummary(filters);
  const heatmap = useAnalyticsHeatmap(filters);
  const scatter = useAnalyticsScatter(filters);
  // Phase B Blocks 2 + 3 — filter-free standalone blocks, kept under the
  // ['analytics'] query namespace alongside the rest.
  const processingTime = useAnalyticsProcessingTime();
  const selfAssessment = useAnalyticsSelfAssessment();
  const activity = useAnalyticsActivity();

  // C1.3 UX — on a filter change keepPreviousData keeps the previous cohort on
  // screen while the new one loads. Surface an explicit «Обновляем…» marker so
  // the stale cohort never reads as the final result for the new filter.
  // `isPlaceholderData` is true exactly while previous data is shown during the
  // background fetch for the new filter key.
  const isRefreshing =
    summary.isPlaceholderData || heatmap.isPlaceholderData || scatter.isPlaceholderData;

  return (
    <>
      <AnalyticsFilters value={filters} onChange={onFilterChange} />

      {isRefreshing && (
        <div
          className="analytics-refreshing"
          data-testid="analytics-refreshing"
          role="status"
          aria-live="polite"
        >
          Обновляем…
        </div>
      )}

      {summary.isLoading && (
        <div className="analytics-empty">загрузка сводки…</div>
      )}
      {summary.isError && (
        <div className="analytics-error">
          ошибка загрузки сводки:{' '}
          {(summary.error as Error)?.message ?? 'неизвестная'}
        </div>
      )}
      {summary.data && <SummaryCards data={summary.data} />}

      {/* Phase R — «Провалы по категориям». Read-only, no filter coupling.
          The same strip used on the Feed screen — keeps the operator's
          mental model consistent. */}
      <section data-testid="analytics-failure-breakdown">
        <h3 className="analytics-section-title">провалы по категориям</h3>
        {/* C4a — each chip deep-links to the Feed pre-filtered to that R
            category, reusing the Feed's existing ``?failure_category=``
            contract. Client-side nav (navigate, no raw <a href>) so Back
            returns to Analytics without a document reload. */}
        <FailureBreakdownStrip
          breakdown={failureBreakdown.data}
          onCategoryClick={(cat) =>
            navigate(failureCategoryFeedUrl(cat), {
              state: ANALYTICS_FAILURE_DRILL_STATE,
            })
          }
        />
      </section>

      {/* Phase I — activity heatmap (GitHub-style) с тоггл-переключателем
          производительность/покрытие. Стоит сверху основной station×config
          heatmap — общий пульс активности предваряет детали. */}
      {activity.isLoading && (
        <div className="analytics-empty">загрузка активности…</div>
      )}
      {activity.data && (
        <ActivityHeatmap
          byCompletion={activity.data.by_completion}
          byObservation={activity.data.by_observation}
        />
      )}

      {heatmap.isLoading && (
        <div className="analytics-empty">загрузка heatmap…</div>
      )}
      {heatmap.data && <HeatmapStationConfig data={heatmap.data} />}

      {scatter.isLoading && (
        <div className="analytics-empty">загрузка scatter…</div>
      )}
      {scatter.data && <ScatterLatitudeResidual points={scatter.data.points} />}

      {/* Phase B Block 3 — service self-assessment adequacy. */}
      {selfAssessment.isLoading && (
        <div className="analytics-empty">загрузка самооценки…</div>
      )}
      {selfAssessment.data && <SelfAssessmentBlock data={selfAssessment.data} />}

      {/* Phase B Block 2 — processing time by config. */}
      {processingTime.isLoading && (
        <div className="analytics-empty">загрузка времени обработки…</div>
      )}
      {processingTime.data && (
        <ProcessingTimeTable
          rows={processingTime.data.processing_time_by_config}
        />
      )}
    </>
  );
}
